import React from "react";
import { Button, Grid, Paper, Select, Typography } from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import { CircularProgress } from "@material-ui/core";
import PageDomain, { NOTIFICATION_PREFERENCE_ITEM_STATUS } from "../../domain";

import "./style.css";
import Switch from "react-input-switch";

const ActionExecutor = ({
  itemStatus = NOTIFICATION_PREFERENCE_ITEM_STATUS.NONE,
  onSave,
  onReset,
  error,
}) => {
  return (
    <Grid
      container
      direction="row"
      className="gap-x-8 my-1"
      justifyContent="flex-end"
    >
      {itemStatus === NOTIFICATION_PREFERENCE_ITEM_STATUS.UPDATED && (
        <>
          <Button className="action-button save" size="small" onClick={onSave}>
            save
          </Button>
          <Button
            className="action-button reset"
            size="small"
            onClick={onReset}
          >
            reset
          </Button>
        </>
      )}
      {itemStatus === NOTIFICATION_PREFERENCE_ITEM_STATUS.SAVING && (
        <Button
          className="action-button saving"
          startIcon={<CircularProgress size={14} />}
        >
          saving
        </Button>
      )}
      {itemStatus === NOTIFICATION_PREFERENCE_ITEM_STATUS.SAVED && (
        <Button
          className="action-button saved"
          startIcon={<CheckCircleIcon size={14} />}
        >
          saved
        </Button>
      )}
      {itemStatus === NOTIFICATION_PREFERENCE_ITEM_STATUS.ERROR && (
        <Typography class="action-error">
          {error}
          <span className="try-again">try again</span>
        </Typography>
      )}
    </Grid>
  );
};

const AccountPreferenceItem = ({
  field,
  label,
  initialValue,
  getUserAccount,
  memberId,
}) => {
  const [value, setValue] = React.useState(initialValue);
  const [currentStatus, setCurrentStatus] = React.useState(
    NOTIFICATION_PREFERENCE_ITEM_STATUS.NONE
  );
  const [error, setError] = React.useState("");
  const initialValueRef = React.useRef(initialValue);

  const updateAccountPreference = async () => {
    PageDomain.updateAccountPreference({
      field,
      value,
      setItemStatus: setCurrentStatus,
      getUserAccount,
      memberId,
      setError,
    });
  };

  const resetPreference = () => {
    setValue(initialValueRef.current);
    setCurrentStatus(NOTIFICATION_PREFERENCE_ITEM_STATUS.NONE);
  };

  return (
    <Grid container direction="column">
      <Grid
        container
        direction="row"
        alignItems="center"
        className="account-preference-item"
      >
        <Typography className="title">{label}</Typography>
        <Switch
          on={true}
          off={false}
          value={value}
          onChange={(value) => {
            setValue(value);
            setCurrentStatus(NOTIFICATION_PREFERENCE_ITEM_STATUS.UPDATED);
          }}
        />
      </Grid>
      <ActionExecutor
        itemStatus={currentStatus}
        onSave={updateAccountPreference}
        onReset={resetPreference}
        error={error}
      />
    </Grid>
  );
};

const NotificationPreferenceItem = ({
  notification,
  options,
  userAccount,
  getUserAccount,
}) => {
  const [itemStatus, setItemStatus] = React.useState(
    NOTIFICATION_PREFERENCE_ITEM_STATUS.NONE
  );
  const UIMeta = PageDomain.getNotificationItemUIMeta({ notification });
  const _options = Array.isArray(options) ? options : [];
  const defaultValue = _options.find((item) => item.isDefault)?.option.id ?? "";
  const userOverrides = userAccount.pushNotificationPreferences ?? [];
  const userOverridedValue = userOverrides.find((entity) => {
    return entity.notification.id === notification.id;
  })?.option.id;
  const computedValue = userOverridedValue ?? defaultValue;

  const valueRef = React.useRef(computedValue);
  const [value, setValue] = React.useState(computedValue);
  const [error, setError] = React.useState("");

  const onChange = (event) => {
    event.preventDefault();
    const newValue = event.target.value;
    setValue(newValue);
    const nextItemStatus =
      newValue === valueRef.current
        ? NOTIFICATION_PREFERENCE_ITEM_STATUS.NONE
        : NOTIFICATION_PREFERENCE_ITEM_STATUS.UPDATED;
    setItemStatus(nextItemStatus);
  };

  const onClickReset = (event) => {
    event.preventDefault();
    setValue(computedValue);
    setItemStatus(NOTIFICATION_PREFERENCE_ITEM_STATUS.NONE);
  };

  const onClickSave = () => {
    PageDomain.updatePushNotificationPreference({
      memberId: userAccount.memberId,
      notification,
      newValue: value,
      setItemStatus,
      getUserAccount,
      setError,
    }).then(() => {
      valueRef.current = value;
    });
  };

  return (
    <Grid item container direction="column" className="notification-item">
      <Typography variant="body2" className="topic text-capitalize font-medium">
        {UIMeta.label}
      </Typography>
      <Typography variant="caption" className="description text-capitalize">
        {UIMeta.description}
      </Typography>
      <Select
        variant="outlined"
        native
        fullWidth
        value={value}
        name={notification.id}
        onChange={onChange}
      >
        {_options.map(({ option }) => (
          <option key={option.id} value={option.id}>
            {option.name}
          </option>
        ))}
      </Select>
      <Grid item container direction="row" className="gap-x-8 my-1">
        {itemStatus === NOTIFICATION_PREFERENCE_ITEM_STATUS.UPDATED && (
          <>
            <Button
              className="action-button save"
              size="small"
              onClick={onClickSave}
            >
              save
            </Button>
            <Button
              className="action-button reset"
              size="small"
              onClick={onClickReset}
            >
              reset
            </Button>
          </>
        )}
        {itemStatus === NOTIFICATION_PREFERENCE_ITEM_STATUS.SAVING && (
          <Button
            className="action-button saving"
            startIcon={<CircularProgress size={14} />}
          >
            saving
          </Button>
        )}
        {itemStatus === NOTIFICATION_PREFERENCE_ITEM_STATUS.SAVED && (
          <Button
            className="action-button saved"
            startIcon={<CheckCircleIcon size={14} />}
          >
            saved
          </Button>
        )}
        {itemStatus === NOTIFICATION_PREFERENCE_ITEM_STATUS.ERROR && (
          <Typography class="action-error">
            {error}
            <span className="try-again">try again</span>
          </Typography>
        )}
      </Grid>
    </Grid>
  );
};

const NotificationPreferences = ({ userAccount, getUserAccount }) => {
  const [allowedPreferences, setAllowedPerferences] = React.useState([]);

  React.useEffect(() => {
    PageDomain.getAllowedNotificationPreferences({
      memberId: userAccount.memberId,
      setAllowedPerferences,
    });
  }, [userAccount]);

  return (
    <Paper elevation={0} className="notification-preference-section">
      <Grid
        container
        direction="column"
        className="complete-ad-div settings-container post-details-con gap-y-16"
      >
        <Grid container>
          <Typography variant="h6" className="heading">
            Preferences
          </Typography>
          <Grid
            item
            container
            direction="column"
            className="notification-preferences"
          >
            <Typography variant="subtitle1">
              Notification Preferences
            </Typography>
            <Grid
              item
              container
              direction="row"
              alignItems="flex-end"
              className="content-section notification-item-list"
            >
              {allowedPreferences.map(({ notification, options }) => (
                <Grid
                  item
                  container
                  className="notification-preference-item-container"
                  key={notification.id}
                >
                  <NotificationPreferenceItem
                    notification={notification}
                    options={options}
                    userAccount={userAccount}
                    getUserAccount={getUserAccount}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
        <Grid
          container
          direction="column"
          className="account-preferences gap-y-8"
        >
          <Typography variant="subtitle1">Account Preferences</Typography>
          <Grid
            container
            direction="column"
            className="content-section gap-y-8"
          >
            <Grid item className="notification-preference-item-container">
              <AccountPreferenceItem
                field="enableLastOnline"
                initialValue={userAccount.userPreferences.enableLastOnline}
                label="enable last online status"
                getUserAccount={getUserAccount}
                memberId={userAccount.memberId}
              />
            </Grid>
            <Grid item className="notification-preference-item-container">
              <AccountPreferenceItem
                field="enableHytch"
                initialValue={userAccount.userPreferences.enableHytch}
                label="enable hytch"
                getUserAccount={getUserAccount}
                memberId={userAccount.memberId}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default NotificationPreferences;
